import {useMutation} from '@apollo/client'
import React, {useEffect} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {parseId} from '../../helpers'
import {ADD_EDIT_STAGE} from '../../gql/mutations/pipelineMutations'
import {IResAddStage, StageType} from '../../types/pipelines'

interface IModalAddStages {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit?: null | {id: string; name: string; order: number; stageType?: StageType | null}
  pipelineId: string
  maxOrder: number
}

const asM = defineMessages({
  addTitle: {id: 'leads.stages.addTitle', defaultMessage: 'Add stage'},
  editTitle: {id: 'leads.stages.editTitle', defaultMessage: 'Edit stage'},
  nameLabel: {id: 'leads.stages.nameLabel', defaultMessage: 'Stage name'},
  classification: {id: 'leads.stages.classification', defaultMessage: 'Classification'},
  typeNone: {id: 'leads.stages.typeNone', defaultMessage: 'None'},
  typeNew: {id: 'leads.stages.typeNew', defaultMessage: 'New'},
  typeDeclined: {id: 'leads.stages.typeDeclined', defaultMessage: 'Declined'},
  toastAdded: {id: 'leads.stages.toast.added', defaultMessage: 'Stage added successfully'},
  toastEdited: {id: 'leads.stages.toast.edited', defaultMessage: 'Stage edited successfully'},
})

export const ModalAddStage: React.FC<IModalAddStages> = ({
  isOpen,
  onClose,
  refetch,
  infoEdit,
  pipelineId,
  maxOrder,
}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})

  const [gqlAddEditStage, {data, loading, error}] = useMutation<IResAddStage>(ADD_EDIT_STAGE)

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.stagePipelineCreateupdateMutation?.stagePipeline?.id) {
      toast.success(intl.formatMessage(infoEdit ? asM.toastEdited : asM.toastAdded))
      onClose()
      refetch()
    } else if (data.stagePipelineCreateupdateMutation?.errors?.length > 0) {
      toast.error(errMsg(data.stagePipelineCreateupdateMutation?.errors?.[0]?.messages?.[0] || intl.formatMessage({id: 'leads.add.toast.somethingHappened', defaultMessage: 'something happened'})))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const {name, stageType} = Object.fromEntries(new FormData(e.currentTarget))
    gqlAddEditStage({
      variables: {
        input: {
          id: infoEdit ? parseId(infoEdit.id) : undefined,
          name,
          pipeline: pipelineId,
          order: infoEdit ? infoEdit.order : maxOrder + 1,
          stageType: stageType || null,
        },
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(infoEdit ? asM.editTitle : asM.addTitle)}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <Form.Group className='mb-3' controlId='exampleForm.ControlInput1'>
            <Form.Label>{intl.formatMessage(asM.nameLabel)}</Form.Label>
            <Form.Control
              type='name'
              name='name'
              defaultValue={infoEdit ? infoEdit.name : ''}
              required
              placeholder={intl.formatMessage({id: 'leads.pipelines.enterName', defaultMessage: 'Enter name'})}
            />
          </Form.Group>
          <Form.Group className='mb-3' controlId='stageType'>
            <Form.Label>{intl.formatMessage(asM.classification)}</Form.Label>
            <Form.Select name='stageType' defaultValue={infoEdit?.stageType || ''}>
              <option value=''>{intl.formatMessage(asM.typeNone)}</option>
              <option value='NEW'>{intl.formatMessage(asM.typeNew)}</option>
              <option value='SOLD'>{intl.formatMessage({id: 'leads.detail.sold', defaultMessage: 'Sold'})}</option>
              <option value='DECLINED'>{intl.formatMessage(asM.typeDeclined)}</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          </Button>
          <Button
            type='submit'
            variant='primary'
            className='d-flex align-items-center'
            disabled={loading}
          >
            {loading && <Spinner animation='border' size='sm' />}
            <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
